import {
  AfterViewInit,
  Component,
  effect,
  OnInit,
  PLATFORM_ID,
  Signal,
  WritableSignal,
  afterNextRender,
  computed,
  inject,
  signal
} from '@angular/core';
import { NavigationStart, Router, Event, NavigationEnd, NavigationError, NavigationCancel, ActivatedRoute, RouterOutlet } from '@angular/router';
import { StateService } from './modules/shared/services/storage/state.service';
import { StoreDetailService } from './modules/shared/services/resolver/store-detail.service';
import { DynamicMetaService } from './modules/shared/services/dynamic-meta.service';
import { TitleService } from './modules/shared/services/title.service';
import {
  MatDialog,
  MatDialogRef,
  MatDialogState
} from '@angular/material/dialog';
import { CookieSettingsComponent } from './modules/components/cookie-settings/cookie-settings.component';
import { CookieService } from './modules/shared/services/cookie.service';
import { PaginationService } from './modules/shared/services/pagination/pagination.service';
import { BinanceService } from './modules/shared/services/binance/binance.service';
import { DynamicScript } from './modules/shared/services/dynamic-script.service';
import { ClarityService } from './modules/shared/services/clarity/clarity.service';
import { GateIoService } from './modules/shared/services/gate-io/gate-io.service';
import { filter, switchMap, take } from 'rxjs/operators';
import { ConsentService } from './modules/shared/services/consent.service';
import { LocalStorageService } from './modules/shared/services/storage/local-storage.service';
import { Profile } from './modules/shared/models/profile.mode';
import { Observable, of, Subscription } from 'rxjs';
import { isPlatformBrowser, AsyncPipe, ViewportScroller } from '@angular/common';
import { LocalStorageVariables } from './modules/shared/enum/localstorage-variables';
import { StoreRegionService } from './modules/shared/services/store-region.service';
import { RegionRoutingService } from './modules/shared/services/region-routing.service';
import { Auth, GoogleAuthProvider, signInWithCredential } from '@angular/fire/auth';
import { TotpCodeDialogComponent } from 'src/app/modules/authenticate/components/totp-code-dialog/totp-code-dialog.component';
import { AuthenticateService } from './modules/authenticate/services/authenticate.service';
import { MetaPixelService } from './modules/shared/services/meta-pixel/meta-pixel.service';
import { GoogleTagManagerService } from './modules/shared/services/google-tag-manager.service';
import { ProfileResolverService } from './modules/shared/services/resolver/profile-resolver.service';
import { FACEBOOK_EVENT_TYPES } from './modules/shared/services/meta-pixel/meta-pixel.model';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ToastContainerComponent } from './modules/shared/components/toast/toast-container.component';
import { ChannelSourceService } from './modules/shared/services/channel-source/channel-source.service';
import { CHANNEL_SOURCE } from './modules/shared/services/channel-source/channel-source.model';
import { GoogleService } from './modules/authenticate/services/google.service';
import { ToastService } from './modules/shared/services/toast.service';
const TOTP_REQUIRED_ERROR_CODE = 4066;
const TOTP_INVALID_ERROR_CODE = 4067;
@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: true,
    imports: [MatProgressSpinner, RouterOutlet, AsyncPipe, ToastContainerComponent]
})
export class AppComponent implements OnInit, AfterViewInit {
  private stateService = inject(StateService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private storeDetailService = inject(StoreDetailService);
  private titleService = inject(TitleService);
  private dynamicMetaService = inject(DynamicMetaService);
  private cookieService = inject(CookieService);
  private paginationService = inject(PaginationService);
  private loadScript = inject(DynamicScript);
  private binanceService = inject(BinanceService);
  private gateIoService = inject(GateIoService);
  private dialog = inject(MatDialog);
  private clarityService = inject(ClarityService);
  private consentService = inject(ConsentService);
  private localStorageService = inject(LocalStorageService);
  private storeRegionService = inject(StoreRegionService);
  private regionRoutingService = inject(RegionRoutingService);
  private auth = inject(Auth);
  private toastService = inject(ToastService);
  private authenticateService = inject(AuthenticateService);
  private profileResolver = inject(ProfileResolverService);
  private googleTagManagerService = inject(GoogleTagManagerService);
  private metaPixelService = inject(MetaPixelService);
  private channelSourceService = inject(ChannelSourceService);
  private googleService = inject(GoogleService);
  public showLoading: boolean;
  public isBlock: boolean = false;
  public isOffline: boolean = false;
  public isBrowser: boolean;
  storeDetail: any;
  isLoggedIn: boolean = false;
  cookieDialogRef: MatDialogRef<any>;
  isClubMember: boolean = false;
  userProfile: Profile;
  loading: Observable<boolean>;
  private readonly freshworksRoutes = ['/profile/orderHistory'];
  startUrl: string;
  platformId = inject(PLATFORM_ID);
  currentChannelSource : WritableSignal<CHANNEL_SOURCE | null> = signal(null);
  isBinance :  Signal<boolean> = computed(() => this.currentChannelSource() === CHANNEL_SOURCE.BINANCE);
  isGateIo : Signal<boolean> = computed(() => this.currentChannelSource() === CHANNEL_SOURCE.GATEIO);
  canShowOneTap = computed(() => !this.isBinance() && !this.isGateIo())
  private scroller = inject(ViewportScroller);
  private isProfileFetchInProgress = false;
  private profileSyncEnabled = signal(false);
  private binanceEmbeddedLoginScheduled = false;
  private gateIoEmbeddedAuthScheduled = false;
  private readonly isFeedbackInitialRoute = this.detectFeedbackInitialRoute();
  private readonly isUnsubscribeInitialRoute = this.detectUnsubscribeInitialRoute();
  
  constructor() {
    this.isBrowser = isPlatformBrowser(this.platformId);
    effect(() => {
      const s = this.channelSourceService.source_();
      if (s !== null) {
        this.currentChannelSource.set(s);
      }
    });
    effect(() => {
      if(this.canShowOneTap() && this.isClientPlatform() && !this.isLoggedIn) {
        this.googleService.init(this.handleCredentialResponse);
        setTimeout(() => {
          this.googleService.showOneTap();
        }, 100);
      }
    });
    effect(() => {
      const isProfileSyncEnabled = this.profileSyncEnabled();
      if (!isProfileSyncEnabled || !isPlatformBrowser(this.platformId)) {
        return;
      }

      const isLoggedIn = this.stateService.isLoggedIn_();
      if (!isLoggedIn) {
        this.userProfile = null;
        return;
      }

      const existingProfile = this.stateService.userProfile();
      if (existingProfile || this.isProfileFetchInProgress) {
        return;
      }
      if (this.isFeedbackInitialRoute || this.isUnsubscribeInitialRoute) {
        return;
      }

      this.isProfileFetchInProgress = true;
      this.profileResolver.updateInfo().subscribe({
        next: () => {
          this.isProfileFetchInProgress = false;
        },
        error: () => {
          this.isProfileFetchInProgress = false;
        }
      });
    });
    afterNextRender(() => {
      if (!isPlatformBrowser(this.platformId)) {
        return;
      }
      setTimeout(() => this.tryEmbeddedExchangeAuthentication(), 0);
      setTimeout(() => this.tryEmbeddedExchangeAuthentication(), 150);
    });
  }

  ngOnInit() {
    if (this.isBrowser) {
      this.profileSyncEnabled.set(true);
      const accessToken = this.localStorageService.getItem(
        LocalStorageVariables.access_token
      );
      const refreshToken = this.localStorageService.getItem(
        LocalStorageVariables.refresh_token
      );
      this.stateService.hydrateAuthTokensFromStorage(accessToken, refreshToken);
      this.stateService.setLoginStatus(!!accessToken && !!refreshToken);
    } else {
      this.stateService.clearAuthTokens();
      this.stateService.setLoginStatus(false);
    }
    this.loading = this.stateService.getLoadingStatus();

    this.setLoginStatus();
    this.checkRoute();
    this.setScripts();
    if (isPlatformBrowser(this.platformId)) {
      this.googleService.init(this.handleCredentialResponse);
    }
    this.consentService.currentConsent$.subscribe((consent) => {
      if (isPlatformBrowser(this.platformId)) {
        if (
          typeof window !== 'undefined' &&
          typeof window.gtag === 'function'
        ) {
          window.gtag(
            'config',
            `${this.storeDetailService?.permissionInit?.firebase_config?.measurementId}`
          );
        }
      }
    });
    this.router.events
      .pipe(
        filter(
          (event) =>
            event instanceof NavigationEnd || event instanceof NavigationStart
        )
      )
      .subscribe((event: NavigationEnd | NavigationStart) => {
        if (event instanceof NavigationStart) {
          this.startUrl = event.url;
        }
        if (event instanceof NavigationEnd) {
          if(!isPlatformBrowser(this.platformId)) return;
          this.googleTagManagerService.loadGoogleAnalytics();
          if(this.canShowOneTap() && !this.isLoggedIn) {
            this.googleService.showOneTap();
          }
          if (
            this.freshworksRoutes.some((route) =>
              event.urlAfterRedirects.startsWith(route)
            )
          ) {
            if (!(window as any).FreshworksWidget) {
              this.loadScript.loadFreshwork().catch(() => {});
            } else {
              this.loadScript.showFreshWorksWidget();
            }
          } else {
            if ((window as any).FreshworksWidget) {
              this.loadScript.removeFreshWorkScript();
            }
          }
            // Only handle automatic region redirection if RegionRoutingService is not already handling it
            if (
              (event.url === '/' || event.url === '') &&
              !this.regionRoutingService.isHandlingRegionNavigation() &&
              this.startUrl != undefined &&
              this.startUrl === event.url
            ) {
              const selectedRegion = this.storeRegionService.selectedRegion;
              if (selectedRegion?.has_homepage) {
                this.router.navigate(['/', selectedRegion.code]);
              } else {
                this.router.navigate(['/'], {
                  queryParams: { region: selectedRegion?.code }
                });
              }
            }
            this.regionRoutingService.endHandlingRegionNavigation();
        }
      });


  }

  ngAfterViewInit(): void {
    if (!this.isBrowser) {
      return;
    }
    // Defer past this CD cycle (same FCP rationale as before); ngAfterViewInit runs after the
    // root view is up, so we do not depend on catching the first NavigationEnd (often missed on Angular 15+).
    setTimeout(() => {
      this.clarityService.init();
      this.googleTagManagerService.loadGoogleAnalytics();
    }, 0);
  }

  private detectFeedbackInitialRoute(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    const { pathname } = window.location;
    return pathname === '/feedback' || pathname.startsWith('/feedback/');
  }

  private detectUnsubscribeInitialRoute(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }
    const { pathname } = window.location;
    return pathname === '/unsubscribe' || pathname.startsWith('/unsubscribe/');
  }

  isClientPlatform(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  checkCookieDialog() {
    if (
      this.cookieDialogRef &&
      this.cookieDialogRef.getState() === MatDialogState.OPEN
    )
      return;

    const showDialog =
      !this.binanceService.isBinance &&
      !this.gateIoService.isGateIo() &&
      !this.cookieService.isConfirmed;
    if (showDialog) {
      this.cookieDialogRef = this.dialog.open(CookieSettingsComponent, {
        disableClose: true,
        hasBackdrop: false,
        maxWidth: '100vh',
        position: { bottom: '62px', left: '16px', right: '16px' },
        panelClass: 'cookie-dialog'
      });
    }
  }

  setLoginStatus() {
    this.stateService
      .getLoginStatus()
      .pipe(
        switchMap((val) => {
          this.isLoggedIn = val;
          if (val === true) {
            return this.stateService.getUserProfile();
          } else {
            return of(null);
          }
        })
      )
      .subscribe((profileData: Profile | null) => {
        if (profileData !== null) {
          this.userProfile = profileData;
          this.stateService.setUserProfile(
            profileData
          );
        }
      });
  }

  /**
   * Binance / Gate.io embedded WebViews cannot be detected during SSR and the Binance
   * bridge often appears after the first paint. Run auth only in the browser after
   * channel and bridge detection have caught up.
   */
  private tryEmbeddedExchangeAuthentication(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    if (
      this.localStorageService.getItem(LocalStorageVariables.access_token)
    ) {
      return;
    }

    this.channelSourceService.initializeClientEnvironment();

    if (this.binanceService.isBinance) {
      if (!this.binanceEmbeddedLoginScheduled) {
        this.binanceEmbeddedLoginScheduled = true;
        this.binanceService.autoLoginBinance(false, undefined);
      }
      return;
    }

    if (
      this.gateIoService.isGateIo() &&
      this.localStorageService.getItem(LocalStorageVariables.has_gate) !==
        'true'
    ) {
      if (this.gateIoEmbeddedAuthScheduled) {
        return;
      }
      this.gateIoEmbeddedAuthScheduled = true;

      const oauthKey = LocalStorageVariables.gateio_oauth_started;
      let gateNavSub: Subscription | undefined;

      const handleGateIoNavigation = (): void => {
        setTimeout(() => {
          this.scroller.scrollToPosition([0, 0]);
        }, 0);
        if (
          this.localStorageService.getItem(LocalStorageVariables.access_token)
        ) {
          gateNavSub?.unsubscribe();
          return;
        }
        if (!isPlatformBrowser(this.platformId)) {
          return;
        }
        if (
          this.route.snapshot.queryParams['code']?.length &&
          window.sessionStorage.getItem(oauthKey) === 'true'
        ) {
          this.gateIoService.gateIoLogin(
            this.route.snapshot.queryParams['code']
          );
          gateNavSub?.unsubscribe();
        } else if (!window.sessionStorage.getItem(oauthKey)) {
          this.gateIoService.redirectToGateAuth();
        }
      };

      gateNavSub = this.router.events
        .pipe(filter((event) => event instanceof NavigationEnd))
        .subscribe(() => {
          if (
            this.localStorageService.getItem(LocalStorageVariables.access_token)
          ) {
            gateNavSub?.unsubscribe();
            return;
          }
          handleGateIoNavigation();
        });

      // Initial route may already have emitted NavigationEnd before we subscribed (SSR hydrate).
      handleGateIoNavigation();
    }
  }

  checkRoute() {
    this.router.events.subscribe((event: Event) => {
      if (event instanceof NavigationStart) {
        this.storeDetail = this.storeDetailService.permissionInit;
        this.titleService.updateTitle(this.storeDetail?.name);
        this.stateService.startLoading();
      }
      if (
        event instanceof NavigationEnd ||
        event instanceof NavigationError ||
        event instanceof NavigationCancel
      ) {
        setTimeout(() => {
          this.scroller.scrollToPosition([0, 0]);
        }, 0)
        this.paginationService.clearNextPageMethod();
        this.dialog.closeAll();
        if(isPlatformBrowser(this.platformId)){
          this.checkCookieDialog();
        }
        this.stateService.endLoading();
        if (this.router.url.includes('search')) {
          this.stateService.setSearchStatus(true);
        } else {
          this.stateService.setSearchStatus(false);
        }
      }
    });
  }

  setScripts() {
    let storeDetail = this.storeDetailService.permissionInit;
    if (storeDetail?.scripts) {
      this.dynamicMetaService.loadMeta(storeDetail.scripts);
    }
  }

  handleCredentialResponse = (response: any) => {
    const credential = GoogleAuthProvider.credential(response.credential);
    signInWithCredential(this.auth, credential)
      .then((res: any) => {
        this.loginWithGoogle(res._tokenResponse.idToken);
      })
      .catch((error) => {
        console.error('Error signing in with credential:', error);
        this.toastService.show({
          message: 'Failed to sign in with Google.',
          type: 'error',
          duration: 2000
        });

      });
  };

  loginWithGoogle(token: string) {
    this.authenticateService.firebaseLogin({ token }).subscribe(
      {
        next: (res) => {
          this.handleLoginSuccess(res);
        },
        error: (error) => {
          if (
            error?.error?.code == TOTP_REQUIRED_ERROR_CODE ||
            error?.error?.code == TOTP_INVALID_ERROR_CODE
          ) {
            this.handleTotpRequired(token);
          } else {
            this.handleLoginError(error);
          }
        }
      }
    );
  }

  handleTotpRequired(token) {
    const dialogRef = this.dialog.open(TotpCodeDialogComponent, {
      width: '500px',
      panelClass: 'tfa-dialog',
      data: {
        needToVerify: false
      }
    });
    dialogRef.afterClosed().subscribe((totpCode: string) => {
      if (totpCode) {
        this.retryLoginWithTotp(token, totpCode);
      } else return;
    });
  }

  private retryLoginWithTotp(token: string, totpCode: string) {
    this.authenticateService
      .firebaseLogin({
        token,
        totp_code: totpCode
      })
      .subscribe(
        {
          next:(res) => {
            this.handleLoginSuccess(res);
          },
          error:(error) => {
            if (
              error?.error?.code == TOTP_REQUIRED_ERROR_CODE ||
              error?.error?.code == TOTP_INVALID_ERROR_CODE
            ) {
              this.handleTotpRequired(token);
            } else {
              this.handleLoginError(error);
            }
          }
        }
      );
  }
  handleLoginError(error) {}
  handleLoginSuccess(res: any) {
    this.googleTagManagerService.sendAnalytics('login', {
      method: 'Google'
    });
    this.googleTagManagerService.sendAnalytics('conversion_event_signup', {
      method: 'Google'
    });
    this.metaPixelService.pushEvent(
      FACEBOOK_EVENT_TYPES.COMPLETE_REGISTRATION,
      {
        data: {
          currency: 'no-data',
          value: 1,
          content_name: 'Google'
        }
      }
    );
    this.localStorageService.setItem(LocalStorageVariables.access_token, res.token);
    this.localStorageService.setItem(
      LocalStorageVariables.refresh_token,
      res.refresh_token
    );
    this.stateService.setAuthTokens(res.token, res.refresh_token);
    this.stateService.setLoginStatus(true);
    this.profileResolver.updateInfo().subscribe();
    // Reload the page to reflect logged-in state
    if (isPlatformBrowser(this.platformId)) {
      //if we're in login page go to redirect url else reload the page
      if(this.router.url.includes('authenticate/login')) {
        this.router.navigateByUrl(this.localStorageService.getItem(LocalStorageVariables.redirect_url));
      } else {
        window.location.reload();
      }
    } else {
      if(this.router.url.includes('authenticate/login')) {
        this.router.navigateByUrl(this.localStorageService.getItem(LocalStorageVariables.redirect_url));
      } else {
        this.router.navigateByUrl('/');
      }
    }
  }

}
